/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useMemo } from "react"
import { useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { SellingTime } from "types/models/menu"
import { useListSellingTimesQuery } from "queries"
import { getNormalizedSellingTimes } from "utils/selling_time_helper"
import { useEnablePartialMenu } from "./useEnablePartialMenu"

/**
 * Hook to get selling times with support for partial menu feature flag.
 * When enablePartialMenu is true, uses RTK Query to fetch selling times.
 * When enablePartialMenu is false, uses Redux state.
 *
 * @returns Object containing:
 *   - sellingTimes: The array of selling times (from RTK Query or Redux)
 *   - isLoading: Whether the RTK Query is currently loading
 */
export const useSellingTimes = () => {
  const enablePartialMenu = useEnablePartialMenu()

  const {
    merchantID,
    merchantGroupID,
    isSellingTimeStructure,
    sellingTimes: sellingTimesRedux,
  } = useSelector((state: AppState) => ({
    merchantID: state.menu.merchantID,
    merchantGroupID: state.menu.merchantGroupID,
    isSellingTimeStructure: state.menu.isSellingTimeStructure,
    sellingTimes: state.menu.sellingTimes,
  }))

  const { data: sellingTimesData, isLoading } = useListSellingTimesQuery(
    { merchantID, merchantGroupID },
    { skip: !enablePartialMenu || !isSellingTimeStructure || !merchantID },
  )

  // Normalize RTK Query data to include groupedServiceHours
  const normalizedSellingTimesFromRTK = useMemo(
    () =>
      getNormalizedSellingTimes(
        sellingTimesData?.sellingTimes as SellingTime[] | undefined,
      ),
    [sellingTimesData],
  )

  const sellingTimes: SellingTime[] = enablePartialMenu
    ? normalizedSellingTimesFromRTK
    : sellingTimesRedux

  return {
    sellingTimes,
    isLoading: enablePartialMenu && isLoading,
  }
}
